/**
 * OptionDeployPage â€” Phase 3
 * Deployment configuration screen for an option strategy.
 * All state is local â€” no backend calls.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Rocket, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import { optionsApi } from '../../api/client';
import { useBrokerCurrent } from '../../api/hooks';

// â”€â”€â”€ Local helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function strikeLabel(mode, offset = 1) {
  if (mode === 'ATM')     return 'ATM';
  if (mode === 'ITM')     return offset >= 2 ? 'ITM2' : 'ITM1';
  if (mode === 'OTM')     return offset >= 2 ? 'OTM2' : 'OTM1';
  if (mode === 'premium') return 'Premium';
  return mode ?? 'â€”';
}

function expiryShort(val) {
  return (
    { current_week: 'Cur Wk', next_week: 'Next Wk',
      current_month: 'Cur Mo', next_month: 'Next Mo' }[val] ?? val ?? 'â€”'
  );
}

// â”€â”€â”€ Mock strategy data (mirrors OptionStrategyDetailPage) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const MOCK_STRATEGIES = {
  'bull-call-spread': {
    name: 'Bull Call Spread', family: 'bullish',
    underlying: 'NIFTY', expiry: 'current_week', lotSize: 75,
    legs: [
      { action: 'BUY',  optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1 },
    ],
  },
  'bear-put-spread': {
    name: 'Bear Put Spread', family: 'bearish',
    underlying: 'BANKNIFTY', expiry: 'current_week', lotSize: 15,
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1 },
    ],
  },
  'long-straddle': {
    name: 'Long Straddle', family: 'volatility',
    underlying: 'BANKNIFTY', expiry: 'current_week', lotSize: 15,
    legs: [
      { action: 'BUY', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 2 },
      { action: 'BUY', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 2 },
    ],
  },
  'short-straddle': {
    name: 'Short Straddle', family: 'volatility',
    underlying: 'NIFTY', expiry: 'current_week', lotSize: 75,
    legs: [
      { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1 },
    ],
  },
  'iron-condor': {
    name: 'Iron Condor', family: 'neutral',
    underlying: 'NIFTY', expiry: 'current_month', lotSize: 75,
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 2, lots: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1 },
      { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 2, lots: 1 },
    ],
  },
  'iron-fly': {
    name: 'Iron Fly', family: 'neutral',
    underlying: 'FINNIFTY', expiry: 'current_week', lotSize: 40,
    legs: [
      { action: 'BUY',  optionType: 'PE', strikeMode: 'OTM', offset: 1, lots: 1 },
      { action: 'SELL', optionType: 'PE', strikeMode: 'ATM', offset: 0, lots: 1 },
      { action: 'SELL', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1 },
      { action: 'BUY',  optionType: 'CE', strikeMode: 'OTM', offset: 1, lots: 1 },
    ],
  },
};

function getMockStrategy(id) {
  if (MOCK_STRATEGIES[id]) return { id, ...MOCK_STRATEGIES[id] };
  return {
    id,
    name: id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    family: 'custom',
    underlying: 'NIFTY',
    expiry: 'current_week',
    lotSize: 75,
    legs: [{ action: 'BUY', optionType: 'CE', strikeMode: 'ATM', offset: 0, lots: 1 }],
  };
}

const FAMILY_BADGE = {
  bullish:    'bg-bull/10 text-bull border-bull/20',
  bearish:    'bg-bear/10 text-bear border-bear/20',
  neutral:    'bg-info/10 text-info border-info/20',
  volatility: 'bg-amber/10 text-amber border-amber/20',
  custom:     'bg-bg-elevated text-txt-muted border-bg-border',
};

// â”€â”€â”€ Local Section component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function Section({ title, accent, children }) {
  return (
    <div className="panel p-4 space-y-4">
      <div className="flex items-center gap-2.5 border-b border-bg-border/60 pb-3">
        {accent && <span className={cn('w-0.5 h-4 rounded-full shrink-0', accent)} />}
        <h3 className="section-title">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <label className="field-label">{label}</label>
      {children}
      {hint && <p className="text-2xs font-mono text-txt-muted">{hint}</p>}
    </div>
  );
}

// â”€â”€â”€ Summary row â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function SummaryRow({ label, value, valueClass }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-bg-border/30 last:border-0">
      <span className="field-label">{label}</span>
      <span className={cn('text-xs font-mono font-medium', valueClass || 'text-txt-primary')}>{value}</span>
    </div>
  );
}

// â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function OptionDeployPage() {
  const navigate       = useNavigate();
  const { id }         = useParams();
  const location       = useLocation();
  const stateStrategy  = location.state?.strategy ?? null;
  const [strategy, setStrategy] = useState(() => stateStrategy ?? getMockStrategy(id));
  const [strategySource, setStrategySource] = useState(() => (stateStrategy ? 'local' : 'mock'));
  const [isDeploying, setIsDeploying] = useState(false);
  const { data: brokerStatus } = useBrokerCurrent();

  useEffect(() => {
    let isCancelled = false;

    async function loadStrategy() {
      try {
        const remote = await optionsApi.get(id);
        if (isCancelled) return;
        setStrategy(remote);
        setStrategySource('backend');
      } catch {
        if (isCancelled) return;
        if (stateStrategy) {
          setStrategy(stateStrategy);
          setStrategySource('local');
        } else {
          setStrategy(getMockStrategy(id));
          setStrategySource('mock');
        }
      }
    }

    loadStrategy();
    return () => { isCancelled = true; };
  }, [id, stateStrategy]);

  // Deploy config state
  const [deployMode,   setDeployMode]   = useState('paper'); // 'paper' | 'live'
  const [tradingMode,  setTradingMode]  = useState('intraday'); // 'intraday' | 'positional'
  const [entryTime,    setEntryTime]    = useState('09:20');
  const [exitTime,     setExitTime]     = useState('15:15');
  const [maxLossDay,   setMaxLossDay]   = useState(5000);
  const [maxPositions, setMaxPositions] = useState(1);

  const isIntraday = tradingMode === 'intraday';
  const isLive     = deployMode  === 'live';

  async function handleConfirmDeploy() {
    if (isLive && !brokerStatus?.has_active_broker) {
      toast.error('No active broker mapped. Please configure a broker before deploying in LIVE mode.');
      return;
    }
    if (strategySource !== 'backend') {
      toast.error('Strategy is not saved on server. Please save first.');
      return;
    }
    const modeLabel = isLive ? 'Live' : 'Paper';
    const payload = {
      deployMode,
      tradingMode,
      entryTime,
      exitTime: isIntraday ? exitTime : null,
      maxLossDay,
      maxPositions,
      status: 'created',
    };

    setIsDeploying(true);
    try {
      await optionsApi.createDeployment(id, payload);
      toast.success(`${modeLabel} deployment created for "${strategy.name}"`);
      navigate(ROUTES.options(id), {
        state: {
          strategy: {
            ...strategy,
            status: 'deployed',
            deploymentMode: isLive ? 'live' : 'paper',
          },
        },
      });
    } catch (err) {
      toast.error(`Deployment failed: ${err.message}`);
    } finally {
      setIsDeploying(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">

      {/* â”€â”€ 1. Breadcrumb â”€â”€ */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => navigate(ROUTES.options(id))}
          className="btn-ghost p-1.5 shrink-0"
          title="Back to strategy"
        >
          <ArrowLeft size={14} />
        </button>
        <button
          type="button"
          onClick={() => navigate(ROUTES.OPTIONS)}
          className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors"
        >
          O For Option
        </button>
        <ChevronRight size={10} className="text-txt-muted shrink-0" />
        <button
          type="button"
          onClick={() => navigate(ROUTES.options(id))}
          className="text-2xs font-mono text-txt-muted hover:text-txt-secondary transition-colors"
        >
          {strategy.name}
        </button>
        <ChevronRight size={10} className="text-txt-muted shrink-0" />
        <span className="section-title">Deploy</span>
      </div>

      {/* â”€â”€ 2. Mock-state notice â”€â”€ */}
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-bg-border bg-bg-surface/40">
        <Info size={12} className="text-txt-muted shrink-0" />
        <span className="text-2xs font-mono text-txt-muted">
          {strategySource === 'backend'
            ? 'Deployment will create a backend record; UX remains the same.'
            : 'Local fallback mode · if strategy is not persisted yet, deploy stays preview-only.'}
        </span>
      </div>

      {/* â”€â”€ 3. Strategy summary bar â”€â”€ */}
      <div className="panel px-4 py-3 flex items-center gap-4 flex-wrap">
        <span className="font-display font-semibold text-sm text-txt-primary shrink-0">
          {strategy.name}
        </span>
        {strategy.family && (
          <span className={cn('badge border text-2xs font-mono shrink-0', FAMILY_BADGE[strategy.family] ?? FAMILY_BADGE.custom)}>
            {strategy.family}
          </span>
        )}
        <div className="w-px h-4 bg-bg-border hidden sm:block" />
        <span className="text-2xs font-mono text-txt-secondary">
          {strategy.underlying} Â· {expiryShort(strategy.expiry)} Â· lot {strategy.lotSize}
        </span>
        <span className="text-2xs font-mono text-txt-muted">
          {strategy.legs.length} leg{strategy.legs.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* â”€â”€ 4. Two-column form â”€â”€ */}
      <div className="flex flex-col lg:flex-row gap-4">

        {/* Left â€” configuration sections */}
        <div className="flex-1 min-w-0 flex flex-col gap-4">

          {/* A. Deploy Configuration */}
          <Section title="Deploy Configuration" accent="bg-amber/70">
            <div className="grid grid-cols-2 gap-2">
              {/* Paper */}
              <button
                type="button"
                onClick={() => setDeployMode('paper')}
                className={cn(
                  'panel p-3 flex flex-col gap-1 border-2 transition-colors cursor-pointer text-left',
                  isLive
                    ? 'border-bg-border hover:border-bg-hover'
                    : 'border-info/40 bg-info/5'
                )}
              >
                <span className={cn('text-xs font-semibold', !isLive ? 'text-info' : 'text-txt-secondary')}>
                  Paper Trade
                </span>
                <span className="text-2xs font-mono text-txt-muted">Simulated Â· No real orders</span>
              </button>

              {/* Live */}
              <button
                type="button"
                onClick={() => setDeployMode('live')}
                className={cn(
                  'panel p-3 flex flex-col gap-1 border-2 transition-colors cursor-pointer text-left',
                  isLive
                    ? 'border-bear/40 bg-bear/5'
                    : 'border-bg-border hover:border-bg-hover'
                )}
              >
                <span className={cn('text-xs font-semibold', isLive ? 'text-bear' : 'text-txt-secondary')}>
                  Live Trade
                </span>
                <span className="text-2xs font-mono text-txt-muted">Real orders Â· Use caution</span>
              </button>
            </div>

            {isLive && (
              <div className="flex items-start gap-2.5 rounded-lg border border-bear/25 bg-bear/5 px-3 py-2.5">
                <AlertTriangle size={13} className="text-bear shrink-0 mt-0.5" />
                <span className="text-2xs font-mono text-bear leading-relaxed">
                  Live mode will place real orders via broker. Ensure broker connection is configured
                  before proceeding. This is a mock preview â€” no orders are placed from this screen.
                </span>
              </div>
            )}
          </Section>

          {/* B. Scheduling */}
          <Section title="Scheduling" accent="bg-info/50">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Trading Mode">
                <div className="relative">
                  <select
                    value={tradingMode}
                    onChange={(e) => setTradingMode(e.target.value)}
                    className="input-base appearance-none pr-7 cursor-pointer"
                  >
                    <option value="intraday">Intraday</option>
                    <option value="positional">Positional</option>
                  </select>
                  <ChevronRight size={10} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-txt-muted pointer-events-none rotate-90" />
                </div>
              </Field>

              <Field label="Entry Time">
                <input
                  type="time"
                  value={entryTime}
                  onChange={(e) => setEntryTime(e.target.value)}
                  className="input-base"
                />
              </Field>

              {isIntraday && (
                <Field label="Exit Time" hint="Auto-exit at this time if position is open">
                  <input
                    type="time"
                    value={exitTime}
                    onChange={(e) => setExitTime(e.target.value)}
                    className="input-base"
                  />
                </Field>
              )}
            </div>
          </Section>

          {/* C. Risk Controls */}
          <Section title="Risk Controls" accent="bg-bear/60">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Max Loss / Day" hint="Stop new entries once this loss is hit">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-txt-muted text-xs font-mono pointer-events-none">
                    â‚¹
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="500"
                    value={maxLossDay}
                    onChange={(e) => setMaxLossDay(Math.max(0, Number(e.target.value) || 0))}
                    className="input-base pl-7"
                  />
                </div>
              </Field>

              <Field label="Max Positions" hint="Maximum concurrent open legs">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={maxPositions}
                  onChange={(e) => setMaxPositions(Math.max(1, Number(e.target.value) || 1))}
                  className="input-base"
                />
              </Field>
            </div>
          </Section>
        </div>

        {/* Right â€” confirmation summary */}
        <div className="lg:w-[300px] xl:w-[320px] shrink-0">
          <div className="panel p-4 flex flex-col gap-1 sticky top-4">
            <div className="flex items-center gap-2.5 border-b border-bg-border/60 pb-3 mb-1">
              <span className="section-title">Deployment Summary</span>
            </div>

            <SummaryRow label="Strategy"      value={strategy.name} />
            <SummaryRow
              label="Mode"
              value={isLive ? 'Live Trade' : 'Paper Trade'}
              valueClass={isLive ? 'text-bear' : 'text-info'}
            />
            <SummaryRow label="Trading"       value={isIntraday ? 'Intraday' : 'Positional'} />
            <SummaryRow label="Underlying"    value={strategy.underlying} />
            <SummaryRow label="Expiry"        value={expiryShort(strategy.expiry)} />
            <SummaryRow label="Entry"         value={entryTime} />
            {isIntraday && (
              <SummaryRow label="Exit"        value={exitTime} />
            )}
            <SummaryRow
              label="Max Loss / Day"
              value={`â‚¹${maxLossDay.toLocaleString('en-IN')}`}
              valueClass="text-bear"
            />
            <SummaryRow label="Max Positions" value={String(maxPositions)} />

            <p className="text-2xs font-mono text-txt-muted mt-3 pt-3 border-t border-bg-border/40 leading-relaxed">
              All settings are local â€” no data is persisted until backend integration is complete.
            </p>
          </div>
        </div>
      </div>

      {/* â”€â”€ 5. Sticky action bar â”€â”€ */}
      <div className="sticky bottom-0 z-20 -mx-3 md:-mx-5 bg-bg-panel border-t border-bg-border px-4 py-3 flex items-center justify-between gap-3">
        <span className="text-2xs font-mono text-txt-muted hidden sm:inline">
          {isLive
            ? 'âš  Live mode selected â€” real orders would be placed via broker'
            : 'Paper mode â€” simulated execution only'}
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={() => navigate(ROUTES.options(id))}
            className="btn-ghost text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmDeploy}
            disabled={isDeploying}
            className={cn(
              'btn-primary text-xs',
              isLive && 'bg-bear text-white hover:brightness-110'
            )}
          >
            <Rocket size={12} />
            {isDeploying ? 'Deploying…' : 'Confirm Deploy'}
          </button>
        </div>
      </div>

    </div>
  );
}

